import type {
  AiFinding,
  AiReport,
  AuditEntry,
  ContributorLockedView,
  ContributorResult,
  DraftResult,
  GapPack,
  Matrix,
  Me,
  Member,
  NotificationsPayload,
  PortalPack,
  PortalView,
  Project,
  ProjectListItem,
  ProjectPayload,
  PublicConfig,
  QuestionBlock,
  Ranking,
  RankingComparison,
  ReportModel,
  ScoringLockStatus,
  Seat,
  Signoff,
  TcoReport,
  TeamPayload,
  Template,
  UrsExtractResult,
  VendorResponse,
} from './types';

/**
 * The single place that talks to the API.
 *
 * Session tokens are sent as a Bearer token, read from whichever Storage
 * `tokenStorage()` picks below; the cookie is still sent for same-origin
 * browsers.
 */

const TOKEN_KEY = 'hs.session';

/**
 * Requirement (product owner, 24 Sep): sign out when the browser tab closes.
 * `localStorage` survives the tab closing, by design; `sessionStorage` does
 * not, and that is the entire fix — nothing else about how a token is read,
 * written or sent needs to change.
 *
 * Two surfaces read and write this same key through `getToken`/`setToken`
 * and were checked individually rather than moved by reflex:
 *
 * - The ordinary browser app (`/app/*`, `/admin/*`) — moved to
 *   `sessionStorage`. This is the surface the requirement is about.
 * - A Microsoft Teams tab (`web/src/lib/teams.tsx`, `web/src/app/teams/*`,
 *   `packages/teams/`) loads the same bundle inside an iframe *Teams* owns,
 *   not a browser tab the person opened and can close. Teams recycles that
 *   iframe on its own schedule — switching channels, the host app going to
 *   the background, especially on Teams mobile — with no connection to the
 *   person choosing to leave. If the token lived in `sessionStorage` there,
 *   every recycle would look like closing the tab; without Teams SSO
 *   configured on the instance (`config.teamsSso`) the only way back in is
 *   a blocking popup (`signInPopup` in lib/teams.tsx), so ordinary use of a
 *   Teams tab would sign someone out under their feet. Kept on
 *   `localStorage`, matching teams.tsx's own framing: "the tab and the
 *   browser hold a session for the same account" — a Teams tab's lifecycle
 *   is Teams closing, not the iframe reloading.
 * - The embed surface (`web/src/embed/EmbedView.tsx`, served from
 *   `/api/public/embed/:token`) never calls `getToken`/`setToken` at all —
 *   a viewer holds no session, only the link token already in the URL
 *   (`api.embedView` below goes through with `anonymous: true`). There is
 *   nothing to move for that surface; recorded here so the next reader does
 *   not go looking for a second storage path that does not exist.
 *
 * `looksLikeTeamsHost()` below is a narrow, deliberate duplicate of the
 * pre-check `looksLikeTeams()` in `lib/teams.tsx`, not an import of it:
 * that module is React-flavoured (a `.tsx` file) and lazily loads the Teams
 * SDK, and this is a plain data module several non-React surfaces import.
 * The two are allowed to drift — the worst case of this copy being wrong
 * is an extra sign-in prompt, not a security decision, unlike e.g.
 * `pinIndexKey` (src/api/pins.js) which had to be exact everywhere it was
 * duplicated.
 */
function looksLikeTeamsHost(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    // Set explicitly by signInPopup() (lib/teams.tsx) on the popup sign-in
    // page — the one place this can be known without guessing.
    if (params.has('inTeams') || params.get('host') === 'teams') return true;
    // An ordinary browser tab is never framed; Teams always loads the tab
    // inside an iframe it owns, so this alone rules out the overwhelming
    // majority of normal browser use before the weaker signals below run.
    if (window.self === window.top && !window.opener) return false;
    const referrer = document.referrer || '';
    return (
      /teams\.microsoft\.(com|us)|teams\.live\.com|teams\.cloud\.microsoft|msteams/i.test(referrer)
      || /Teams\//i.test(navigator.userAgent)
      || /teams/i.test(window.name || '')
      // A framed page with no usable referrer may still be Teams desktop,
      // which frequently sends none — see looksLikeTeams() in lib/teams.tsx
      // for the same call made the same way.
      || referrer === ''
    );
  } catch {
    return false;
  }
}

function tokenStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return looksLikeTeamsHost() ? window.localStorage : window.sessionStorage;
}

/**
 * The registration terms/privacy links point at Hanseatic LifeScience
 * Consulting's own published agreement and privacy notice — the real legal
 * relationship — not the product's own thin `/imprint` and `/privacy` pages.
 *
 * Kept as a client constant rather than hardcoded inside a component so a
 * private or on-premise instance can point elsewhere. The natural home once
 * the backend carries it would be `/api/public/config` (see `api.config()`
 * below), alongside `publicSite` / `demo` / `aiTrialDays` — this is the
 * client-side default until that lands.
 */
export const LEGAL_LINKS = {
  terms: 'https://www.h-lsc.com/terms',
  privacy: 'https://www.h-lsc.com/privacy',
  imprint: 'https://www.h-lsc.com/imprint',
};

/**
 * `ProjectListItem` plus `everDecided` — see `api.archivedProjects` above
 * for why this is widened here instead of in `./types`.
 */
export type EvaluationSummary = ProjectListItem & {
  everDecided: boolean;
  archivedAt?: string | null;
  archivedBy?: string | null;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  data?: unknown;

  constructor(message: string, status: number, code?: string, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

let memoryToken = '';

export function getToken(): string {
  if (memoryToken) return memoryToken;
  try {
    memoryToken = tokenStorage()?.getItem(TOKEN_KEY) || '';
  } catch {
    memoryToken = '';
  }
  return memoryToken;
}

export function setToken(token: string) {
  memoryToken = token || '';
  try {
    const storage = tokenStorage();
    if (!storage) return;
    if (memoryToken) storage.setItem(TOKEN_KEY, memoryToken);
    else storage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode, or storage blocked: the in-memory token still carries this session */
  }
}

/**
 * The contributor's own unlock proof (api.contributorUnlock below) — always
 * `sessionStorage`, never `tokenStorage()`'s Teams-aware choice above: a
 * contributor link is never opened inside a Teams tab, and this must not
 * survive the tab the way TOKEN_KEY deliberately does there. Keyed by the
 * token itself so two different contributor links opened in two tabs of the
 * same browser profile (sessionStorage is per-tab anyway, but private mode
 * and some embedders share it) never read each other's proof.
 */
function contributorProofKey(token: string): string {
  return `hs.contributor.${token}`;
}

function readContributorProof(token: string): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(contributorProofKey(token)) || '';
  } catch {
    return '';
  }
}

function writeContributorProof(token: string, proof: string) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(contributorProofKey(token), proof);
  } catch {
    /* private mode, or storage blocked — the contributor just re-enters the PIN on reload */
  }
}

export type RequestOptions = {
  body?: unknown;
  signal?: AbortSignal;
  /** Send without a session token (public endpoints). */
  anonymous?: boolean;
  headers?: Record<string, string>;
};

async function parse(res: Response) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

export async function request<T = unknown>(
  method: string,
  url: string,
  { body, signal, anonymous, headers }: RequestOptions = {},
): Promise<T> {
  const token = anonymous ? '' : getToken();
  const res = await fetch(url, {
    method,
    credentials: 'include',
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers || {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await parse(res);
  if (!res.ok) {
    const message = (data && (data.error || data.message)) || res.statusText || 'Request failed';
    throw new ApiError(message, res.status, data?.code, data);
  }
  return data as T;
}

const get = <T>(url: string, opts?: RequestOptions) => request<T>('GET', url, opts);
const post = <T>(url: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', url, { ...opts, body });
const put = <T>(url: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', url, { ...opts, body });
const patch = <T>(url: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', url, { ...opts, body });
const del = <T>(url: string, opts?: RequestOptions) => request<T>('DELETE', url, opts);

export type AiContributorRun = {
  ok: true;
  seat: Seat;
  source: string;
  cellsWritten: number;
  cellsExpected: number;
};

export type SessionPayload = { token: string; user: Me['user']; tenant: Me['tenant']; projectId?: string; expiresAt?: string };

/**
 * Registration is enumeration-safe: an address that already has an account
 * comes back as `{ ok: true, pending: true }` — identical whether or not the
 * account exists — instead of a session.
 *
 * A genuinely new address always gets `verifyLink` when no SMTP is
 * configured, the same way an invitation or reset link is handed back. What
 * comes with it depends on `REQUIRE_EMAIL_VERIFICATION` on the server: off
 * (the default), the account works immediately and a session comes back
 * alongside the link; on, there is no session yet — `requiresVerification`
 * says so — and the link itself is what lets them in (see `api.verifyEmail`).
 */
export type RegisterResult =
  | (SessionPayload & { verifyLink?: string })
  | { ok: true; pending: true }
  | { ok: true; requiresVerification: true; verifyLink: string };

export const api = {
  // --- public ---------------------------------------------------------------
  config: () => get<PublicConfig>('/api/public/config', { anonymous: true }),
  health: () => get<{ ok: boolean; devAuth?: boolean; microsoftAuth?: boolean }>('/api/health', { anonymous: true }),
  lead: (body: Record<string, unknown>) => post<{ ok: true }>('/api/public/leads', body, { anonymous: true }),
  invitation: (token: string) =>
    get<{ email: string; name: string; plan: 'evaluator' | 'contributor'; organisation: string; expiresAt: string | null }>(
      `/api/public/invitations/${encodeURIComponent(token)}`,
      { anonymous: true },
    ),
  acceptInvitation: (token: string, body: { name: string; password: string }) =>
    post<SessionPayload>(`/api/public/invitations/${encodeURIComponent(token)}/accept`, body, { anonymous: true }),
  passwordReset: (token: string) =>
    get<{ email: string; expiresAt: string }>(`/api/public/password-resets/${encodeURIComponent(token)}`, { anonymous: true }),
  usePasswordReset: (token: string, body: { password: string }) =>
    post<SessionPayload>(`/api/public/password-resets/${encodeURIComponent(token)}`, body, { anonymous: true }),
  /**
   * Consume a verification link. When `REQUIRE_EMAIL_VERIFICATION` is on,
   * this is the moment an account minted with no session gets one — the
   * reply carries a full `SessionPayload` alongside `ok`; when it is off,
   * only `{ ok: true }` comes back, since the account already had access.
   */
  verifyEmail: (token: string) =>
    post<{ ok: true } & Partial<SessionPayload>>(`/api/public/verify/${encodeURIComponent(token)}`, {}, { anonymous: true }),
  /** Always a neutral `202` — see the server route for why. */
  resendVerification: (email: string) =>
    post<{ ok: true; pending: true; verifyLink?: string }>('/api/public/verify/resend', { email }, { anonymous: true }),

  // --- auth -----------------------------------------------------------------
  login: (body: { email: string; password: string }) => post<SessionPayload>('/api/auth/login', body, { anonymous: true }),
  /** The read-only shared view, framed into a SharePoint page or an intranet. */
  embedView: (token: string) =>
    get<import('./types').EmbedView>(`/api/public/embed/${encodeURIComponent(token)}`, { anonymous: true }),
  assistantAsk: (body: { message: string; projectId?: string | null; history?: Array<{ role: string; text: string }> }) =>
    post<{
      reply: string;
      assisted?: boolean;
      canEdit?: boolean;
      proposal?: { action: string; args: Record<string, unknown>; describe: string } | null;
    }>('/api/assistant/ask', body),
  assistantAct: (body: { projectId?: string | null; action: string; args: Record<string, unknown> }) =>
    post<{ ok: true; said: string; goTo?: string }>('/api/assistant/act', body),
  embedLinks: (projectId: string) =>
    get<{ links: import('./types').EmbedLink[] }>(`/api/projects/${projectId}/embed-links`),
  createEmbedLink: (projectId: string, body: { label?: string; email?: string }) =>
    post<import('./types').EmbedLink & { link: string; emailed?: boolean }>(`/api/projects/${projectId}/embed-links`, body),
  revokeEmbedLink: (projectId: string, linkId: string) =>
    del<{ ok: true }>(`/api/projects/${projectId}/embed-links/${encodeURIComponent(linkId)}`),

  /** Exchange a Microsoft Teams SSO token for a workspace session. */
  teamsExchange: (ssoToken: string) =>
    post<SessionPayload>('/api/public/teams/exchange', { ssoToken }, { anonymous: true }),
  /**
   * Begin an Entra sign-in: `tenantId` is only read on the shared host, and
   * only once `ssoDiscover` has already named a workspace — on a
   * workspace's own host the server decides from the address instead and
   * this is ignored. The browser is expected to navigate to the returned
   * `authorizeUrl`; nothing here is a fetch the SPA stays on.
   */
  ssoStart: (body: { tenantId?: string; next?: string } = {}) =>
    post<{ authorizeUrl: string }>('/api/public/sso/start', body, { anonymous: true }),
  /** Home-realm discovery: which workspace (if any) an email's domain belongs to. */
  ssoDiscover: (email: string) =>
    get<{ found: boolean; tenantId?: string; name?: string; hostname?: string | null }>(
      `/api/public/sso/discover?email=${encodeURIComponent(email)}`,
      { anonymous: true },
    ),
  register: (body: { email: string; password: string; name: string; orgName: string; acceptedTerms: boolean }) =>
    post<RegisterResult>('/api/auth/register', body, { anonymous: true }),
  logout: () => post<{ ok: true }>('/api/auth/logout', {}),
  me: () => get<Me>('/api/me'),

  // --- workspace ------------------------------------------------------------
  templates: () => get<Template[]>('/api/templates'),
  team: () => get<TeamPayload>('/api/team'),
  invite: (body: { email: string; name?: string; plan: 'evaluator' | 'contributor' }) =>
    post<import('./types').Invitation>('/api/invitations', body),
  revokeInvite: (id: string) => del<{ ok: true }>(`/api/invitations/${encodeURIComponent(id)}`),
  resetLink: (userId: string) => post<{ link: string; emailed: boolean; email: string }>(`/api/team/${encodeURIComponent(userId)}/reset-link`),
  assignPlan: (body: { email?: string; userId?: string; plan: 'evaluator' | 'contributor' }) =>
    post<{ ok: true }>('/api/license/assign', body),
  revokePlan: (userId: string) => post<{ ok: true }>('/api/license/revoke', { userId }),
  activate: (code: string) => post<{ ok: true }>('/api/license/activate', { code }),

  // --- instance admin (requirement 5/6) --------------------------------------
  // Reachable only when `me.user.tenantAdmin` is set — see
  // web/src/app/screens/InstanceAdminScreen.tsx. The server re-checks the
  // same flag on every one of these calls regardless of what the client
  // believes (src/api/routes/tenant-admin.js requireTenantAdmin).
  tenantAdminOverview: () =>
    get<import('./types').TenantAdminOverview>('/api/tenant-admin/overview'),
  tenantAdminUsers: () =>
    get<{ users: import('./types').TenantAdminUser[] }>('/api/tenant-admin/users'),
  tenantAdminSetRole: (userId: string, role: 'evaluator' | 'contributor') =>
    post<{ ok: true }>(`/api/tenant-admin/users/${encodeURIComponent(userId)}/role`, { role }),
  tenantAdminRevokeSessions: (userId: string) =>
    post<{ ok: true }>(`/api/tenant-admin/users/${encodeURIComponent(userId)}/revoke-sessions`, {}),
  tenantAdminRemoveUser: (userId: string) =>
    del<{ ok: true }>(`/api/tenant-admin/users/${encodeURIComponent(userId)}`),
  tenantAdminResetLink: (userId: string) =>
    post<{ link: string; emailed: boolean; email: string }>(`/api/tenant-admin/users/${encodeURIComponent(userId)}/reset-link`),
  tenantAdminInvitations: () =>
    get<{ invitations: import('./types').TenantAdminInvitation[] }>('/api/tenant-admin/invitations'),
  tenantAdminRevokeInvitation: (id: string) =>
    post<{ ok: true }>(`/api/tenant-admin/invitations/${encodeURIComponent(id)}/revoke`, {}),
  /**
   * The locked-evaluation register, scoped to the caller's own tenant
   * (owner, 6 Oct 2026 — moved here from the product operator console,
   * which used to read every tenant's in one list; see
   * src/api/admin/locked-register.js and src/api/admin/tenant-admin.js
   * `lockedEvaluations`, the scoped function this route must call with
   * `req.user.tenantId`, never a value from the request).
   */
  tenantAdminLockedEvaluations: () =>
    get<{ evaluations: import('./types').LockedEvaluation[] }>('/api/tenant-admin/locked-evaluations'),
  /**
   * The Claude-Team-plan equivalent (product owner, 24 Sep): the instance
   * admin's own queue of capacity requests from members of their tenant —
   * never the product operator's console. See src/api/routes/ai-usage.js.
   */
  tenantAdminCreditRequests: () =>
    get<{ requests: import('./types').TenantAdminCreditRequest[] }>('/api/ai/credit-requests'),
  tenantAdminGrantCreditRequest: (id: string, body: { amount: number; note?: string }) =>
    post<{ ok: true; request: import('./types').TenantAdminCreditRequest }>(`/api/ai/credit-requests/${encodeURIComponent(id)}/grant`, body),
  tenantAdminDismissCreditRequest: (id: string) =>
    post<{ ok: true; request: import('./types').TenantAdminCreditRequest }>(`/api/ai/credit-requests/${encodeURIComponent(id)}/dismiss`, {}),

  // Consoles & mail wave (6 Oct 2026): who may lock scoring, and the default
  // a new evaluation's AI toggle starts from — both workspace-wide
  // preferences the instance admin sets, read back as a plain value (see
  // src/api/admin/workspace-console-settings.js for what each one does and
  // does not do yet).
  tenantAdminConsoleSettings: () =>
    get<import('./types').TenantConsoleSettings>('/api/tenant-admin/lock-policy'),
  tenantAdminSetLockPolicy: (lockPolicy: 'owner_only' | 'any_evaluator') =>
    post<import('./types').TenantConsoleSettings>('/api/tenant-admin/lock-policy', { lockPolicy }),
  tenantAdminSetDefaultAiEnabled: (enabled: boolean) =>
    post<import('./types').TenantConsoleSettings>('/api/tenant-admin/default-ai-enabled', { enabled }),

  // A workspace's own criteria templates — distinct from the built-in,
  // read-only industry scorecards in src/shared/templates.mjs (api.templates
  // above): this is a tenant's own saved groups/criteria, visible to nobody
  // else.
  tenantAdminCriteriaTemplates: () =>
    get<{ templates: import('./types').CriteriaTemplate[] }>('/api/tenant-admin/criteria-templates'),
  tenantAdminCreateCriteriaTemplate: (body: { name: string; body: import('./types').CriteriaTemplate['body'] }) =>
    post<import('./types').CriteriaTemplate>('/api/tenant-admin/criteria-templates', body),
  tenantAdminRemoveCriteriaTemplate: (templateId: string) =>
    del<{ ok: true }>(`/api/tenant-admin/criteria-templates/${encodeURIComponent(templateId)}`),

  // --- share by email ---------------------------------------------------------
  /**
   * NOTHING CALLS THESE ANY MORE. "Send by email" is a `mailto:` draft now
   * (`SendLinkControl`, web/src/ui/send.tsx), because no mail transport is
   * configured in production and the server send did nothing useful there.
   * Kept, with their routes (src/api/routes/share-mail.js), until the tech lead
   * decides whether to retire them — a route is not removed on a tidy-up's say-so.
   */
  mailStatus: () => get<{ enabled: boolean }>('/api/mail/status'),
  sendLink: (body: { to: string; subject: string; message?: string; link: string }) =>
    post<{ sent: boolean; error?: string }>('/api/mail/send-link', body),

  // --- projects -------------------------------------------------------------
  /**
   * `locale` is the interface language the person was actually using at the
   * moment of creation ('en' | 'de' only — anything else is nonsense the
   * server would treat as absent anyway, so the type rules it out here
   * instead). It seeds `projects.locale`, which the decision pack prefers
   * over guessing the language from the evaluation's own text (src/shared/
   * deck/**) — this module has no locale context of its own (see
   * `PopupBlockedError` below), so the caller supplies it, the same
   * division of labour as everywhere else in this file. Optional: a caller
   * that could not determine a language omits the field, and the server
   * falls back to detection exactly as before.
   */
  createProject: (body: {
    name: string;
    client?: string;
    description?: string;
    templateId?: string;
    vendors?: Array<{ name: string }>;
    locale?: 'en' | 'de';
  }) => post<ProjectPayload>('/api/projects', body),
  project: (id: string) => get<ProjectPayload>(`/api/projects/${id}`),
  patchProject: (id: string, body: Partial<{ name: string; client: string; scaleMin: number; scaleMax: number; description: string }>) =>
    patch<ProjectPayload>(`/api/projects/${id}`, body),
  ranking: (id: string) => get<Ranking>(`/api/projects/${id}/ranking`),
  /**
   * The ranking computed without and with the AI contributor's scores, for the
   * Decision step's comparison. Aggregate totals only — no contributor, no
   * individual score — so it is safe to ask for while scoring is open.
   */
  rankingCompare: (id: string) => get<RankingComparison>(`/api/projects/${id}/ranking/compare`),
  setStatus: (id: string, status: string, opts?: { reason?: string; acknowledgeLowCoverage?: boolean }) =>
    post<ProjectPayload>(`/api/projects/${id}/status`, { status, ...opts }),
  // --- AI dual-mode (flow upgrade, 5 Oct 2026) — the Decision page's two
  // switches. Both off by default (src/api/db.js setAiContributorCounted /
  // setAiAnalysisIncluded); the server is the one place either write is
  // audited with who and when, so DecisionView calls these rather than
  // patching the project directly.
  setAiContributorCounted: (id: string, enabled: boolean) =>
    post<Project>(`/api/projects/${id}/ai/contributor-counted`, { enabled }),
  setAiAnalysisIncluded: (id: string, enabled: boolean) =>
    post<Project>(`/api/projects/${id}/ai/analysis-included`, { enabled }),
  /**
   * Run (or re-run) the AI contributor's scoring pass — the only thing that
   * gives it a seat and scores, so the only thing that gives the "count the AI
   * contributor" switch anything to count. It was reachable from nowhere in the
   * product until 26 October 2026. A run that cannot have a model FAILS with a
   * coded `ApiError` (see lib/ai-errors.ts) and writes nothing; there is no
   * built-in-rules substitute. `source` is 'contributor-llm' on success.
   */
  runAiContributor: (id: string) =>
    post<AiContributorRun>(`/api/projects/${id}/ai/contributor/run`),
  audit: (id: string) => get<AuditEntry[]>(`/api/projects/${id}/audit`),
  /**
   * `db.listProjectsForUser` (src/api/db.js) sends one field beyond
   * `ProjectListItem`: `everDecided`, whether a filed, signed decision has
   * ever existed for this evaluation — see `EvaluationSummary` below. Widened
   * locally rather than in the shared type, which this surface does not own
   * (the same move `EmbedView.tsx` makes for its own payload).
   */
  archivedProjects: () => get<EvaluationSummary[]>('/api/projects/archived'),
  /**
   * Requirement 3 (product owner, 24 Sep): let a user remove an evaluation
   * from their workspace. The server decides delete vs. archive — never the
   * caller — from whether a decision was ever filed and signed
   * (src/api/db.js `removeProject`): `archived: false` means it is gone for
   * good, `archived: true` means it left the workspace list but still
   * exists and can be brought back with `restoreProject`.
   */
  removeProject: (id: string) => del<{ ok: true; archived: boolean }>(`/api/projects/${id}`),
  restoreProject: (id: string) => post<{ ok: true }>(`/api/projects/${id}/restore`),
  members: (id: string) => get<Member[]>(`/api/projects/${id}/members`),
  addMember: (id: string, body: { email?: string; userId?: string; role: 'evaluator' | 'contributor' }) =>
    post<Member>(`/api/projects/${id}/members`, body),
  removeMember: (id: string, userId: string) => del<{ ok: true }>(`/api/projects/${id}/members/${encodeURIComponent(userId)}`),

  addVendor: (projectId: string, name?: string) => post<{ id: string; name: string }>(`/api/projects/${projectId}/vendors`, { name }),
  patchVendor: (id: string, name: string) => patch<{ id: string; name: string }>(`/api/vendors/${id}`, { name }),
  removeVendor: (id: string) => del<{ ok: true }>(`/api/vendors/${id}`),

  addGroup: (projectId: string, body: { name: string; weight: number }) => post<{ id: string }>(`/api/projects/${projectId}/groups`, body),
  patchGroup: (id: string, body: { name?: string; weight?: number }) => patch<{ id: string }>(`/api/groups/${id}`, body),
  /**
   * A whole column of weights, written together or not at all. The Request
   * step rebalances every row of a column when one is typed; one PATCH per row
   * could leave the column half-saved.
   */
  patchWeights: (
    projectId: string,
    body: { groups?: Array<{ id: string; weight: number }>; criteria?: Array<{ id: string; weight: number }> },
  ) => patch<{ ok: true }>(`/api/projects/${projectId}/weights`, body),
  removeGroup: (id: string) => del<{ ok: true }>(`/api/groups/${id}`),
  addCriterion: (
    groupId: string,
    body: {
      name: string;
      weight: number;
      expectation?: string;
      requirementIds?: string[];
      priority?: 'must' | 'should';
      sourceRef?: string | null;
      editedByUser?: boolean;
    },
  ) => post<{ id: string }>(`/api/groups/${groupId}/criteria`, body),
  patchCriterion: (
    id: string,
    body: {
      name?: string;
      weight?: number;
      expectation?: string;
      requirementIds?: string[];
      priority?: 'must' | 'should';
      sourceRef?: string | null;
      editedByUser?: boolean;
    },
  ) => patch<{ id: string }>(`/api/criteria/${id}`, body),
  removeCriterion: (id: string) => del<{ ok: true }>(`/api/criteria/${id}`),

  addSeat: (projectId: string, body?: string | { name?: string; email?: string; background?: string }) =>
    post<{ id: string }>(`/api/projects/${projectId}/seats`, typeof body === 'string' ? { name: body } : (body || {})),
  patchSeat: (id: string, body: { name?: string; voteWeight?: number; email?: string; background?: string }) =>
    patch<{ id: string }>(`/api/seats/${id}`, body),
  removeSeat: (id: string) => del<{ ok: true }>(`/api/seats/${id}`),
  issuePin: (id: string) => post<{ id: string; pin: string }>(`/api/seats/${id}/pin`),
  resetSeat: (id: string) => post<{ id: string; pin: string }>(`/api/seats/${id}/reset`),
  claimPin: (pin: string) => post<ProjectPayload>('/api/seats/claim', { pin }),

  // --- contributor link (account-free) ---------------------------------------
  // One call covers both first issue and reissue — see db.issueSeatToken's
  // own comment. The plaintext token (and the ready-made link) is handed
  // back exactly once, in this response, same "shown once" contract `pin`
  // already has above. `pin` here is only present when a PIN was actually
  // (re)generated by THIS call — a seat's first link always mints one; a
  // later reissue of the link alone does not (see db.issueSeatToken).
  issueSeatInviteToken: (id: string) => post<Seat & { token: string; link: string; pin?: string }>(`/api/seats/${id}/invite-token`),
  /** A nudge only — never rotates the link (see the route's own comment). */
  remindSeatInvite: (id: string) => post<Seat>(`/api/seats/${id}/invite-token/remind`),
  /** Withdraws the link without deleting the seat or its scores so far. */
  revokeSeatInvite: (id: string) => del<Seat>(`/api/seats/${id}/invite-token`),
  /**
   * Reissue just the PIN, independent of the link (db.issueSeatPin) — the
   * realistic case is "they lost the PIN", and this never touches the link
   * itself. The plaintext PIN is handed back exactly once, here.
   */
  reissueSeatPin: (id: string) => post<Seat & { pin: string }>(`/api/seats/${id}/invite-token/pin`),

  /**
   * The link alone. Returns either `{ pinRequired: true }` (no proof on
   * hand yet in this tab, or it expired) or the full card, read back
   * straight from `sessionStorage` without asking for the PIN again — see
   * contributorUnlock below and src/api/routes/contributor.js's own
   * comment on why this one call covers both.
   */
  contributorView: (token: string) => {
    const proof = readContributorProof(token);
    return get<ProjectPayload | ContributorLockedView>(`/api/contributor/${encodeURIComponent(token)}`, {
      anonymous: true,
      headers: proof ? { 'X-Contributor-Proof': proof } : {},
    });
  },
  /**
   * Exchanges the PIN — handed to the contributor by a route SEPARATE from
   * the link itself (web/src/app/views/SeatsView.tsx's own invitation
   * dialog says so) — for this seat's own card. The `proof` that comes back
   * is kept in `sessionStorage`, never the PIN itself: closing the tab
   * discards it, the same reason the authenticated app's own session token
   * moved there (TOKEN_KEY, above) — this is not a second, laxer place to
   * remember a credential, it is the same decision applied to a surface
   * that has no account to begin with.
   */
  contributorUnlock: async (token: string, pin: string) => {
    const result = await post<ProjectPayload & { proof?: string }>(
      `/api/contributor/${encodeURIComponent(token)}/unlock`,
      { pin },
      { anonymous: true },
    );
    const { proof, ...payload } = result;
    if (proof) writeContributorProof(token, proof);
    return payload as ProjectPayload;
  },
  saveContributorScore: (
    token: string,
    body: { criterionId: string; vendorId: string; value?: number | null; comment?: string },
  ) => put<{ ok: true }>(`/api/contributor/${encodeURIComponent(token)}/scores`, body, {
    anonymous: true,
    headers: { 'X-Contributor-Proof': readContributorProof(token) },
  }),

  saveScore: (projectId: string, body: { criterionId: string; vendorId: string; value?: number | null; comment?: string }) =>
    put<unknown>(`/api/projects/${projectId}/scores`, body),

  // --- results (step 5) ------------------------------------------------------
  // The one-way scoring lock (src/api/scoring-lock.js). Reading it is open to
  // anyone with access to the project (the route itself has no requirePm) —
  // a contributor needs to know it too, since it is what makes their own
  // card read-only. Locking is PM-only and, like the server function it
  // calls, has no corresponding "unlock".
  scoringLockStatus: (projectId: string) => get<ScoringLockStatus>(`/api/projects/${projectId}/scoring-lock`),
  lockScoring: (projectId: string) => post<ScoringLockStatus & { ok: true; alreadyLocked: boolean }>(`/api/projects/${projectId}/scoring-lock`, {}),
  /**
   * Every contributor's own answers — refused by the server with a 403
   * until scoring is locked (src/api/routes/results.js checks the lock
   * itself). Callers must still only ever call this once `scoringLockStatus`
   * says `locked: true` — ResultsView.tsx's query is gated on exactly that,
   * not on this call's own success/failure, so a refusal is never the only
   * thing standing between an open evaluation and an individual answer.
   */
  contributorResults: (projectId: string) => get<{ contributors: ContributorResult[] }>(`/api/projects/${projectId}/results/contributors`),

  // --- evidence -------------------------------------------------------------
  aiReport: (projectId: string) => get<AiReport>(`/api/projects/${projectId}/ai`),
  aiStatus: (projectId: string) => get<{ running: boolean; steps: import('./types').AiStep[] }>(`/api/projects/${projectId}/ai/status`),
  runAi: (projectId: string) => post<AiReport>(`/api/projects/${projectId}/ai/run`),
  gaps: (projectId: string) => get<GapPack>(`/api/projects/${projectId}/ai/gaps`),
  setGapReview: (projectId: string, body: { key: string; flagged: boolean; note: string }) =>
    put<{ review: GapPack['review'] }>(`/api/projects/${projectId}/ai/gap-review`, body),
  uploadDocument: (
    projectId: string,
    body: { kind: string; filename: string; text?: string; contentBase64?: string; vendorId?: string },
  ) => post<{ id: string; clipped?: boolean; clipChars?: number }>(`/api/projects/${projectId}/documents`, body),
  removeDocument: (docId: string) => del<{ ok: true }>(`/api/documents/${docId}`),
  /** The Vendors page's inline viewer — full text, evaluator-only. */
  documentDetail: (docId: string) => get<import('./types').VendorDocumentDetail>(`/api/documents/${encodeURIComponent(docId)}`),
  /** The AI consent gate on one document — never a bulk action. See the column comment on vendor_documents.ai_allowed in schema.sql. */
  setDocumentAiAllowed: (docId: string, aiAllowed: boolean) =>
    patch<{ id: string; aiAllowed: boolean }>(`/api/documents/${encodeURIComponent(docId)}/ai-allowed`, { aiAllowed }),

  /**
   * The AI contributor's findings (src/api/ai-findings.js) — Evidence's left
   * half. `status` filters server-side; left undefined, every finding comes
   * back regardless of decision, since a discarded one must still be shown
   * (marked as discarded), never hidden.
   */
  aiFindings: (projectId: string, status?: string) =>
    get<{ findings: AiFinding[] }>(`/api/projects/${projectId}/ai-findings${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  decideFinding: (projectId: string, findingId: string, decision: 'included' | 'discarded') =>
    post<AiFinding>(`/api/projects/${projectId}/ai-findings/${findingId}/decide`, { decision }),

  // --- document requests (flow upgrade, 5 Oct 2026) --------------------------
  documentRequests: (projectId: string) =>
    get<{ requests: import('./types').DocumentRequest[] }>(`/api/projects/${projectId}/document-requests`),
  createDocumentRequest: (
    projectId: string,
    body: {
      name: string;
      description?: string;
      mandatory?: boolean;
      kind?: import('./types').DocumentRequestKind;
      formats?: string[];
      options?: string[];
      appliesTo?: 'all' | string[];
    },
  ) => post<import('./types').DocumentRequest>(`/api/projects/${projectId}/document-requests`, body),
  patchDocumentRequest: (
    projectId: string,
    requestId: string,
    body: Partial<{
      name: string;
      description: string;
      mandatory: boolean;
      kind: import('./types').DocumentRequestKind;
      formats: string[];
      options: string[];
      appliesTo: 'all' | string[];
    }>,
  ) => patch<import('./types').DocumentRequest>(`/api/projects/${projectId}/document-requests/${encodeURIComponent(requestId)}`, body),
  removeDocumentRequest: (projectId: string, requestId: string) =>
    del<{ ok: true }>(`/api/projects/${projectId}/document-requests/${encodeURIComponent(requestId)}`),
  reorderDocumentRequests: (projectId: string, orderedIds: string[]) =>
    put<{ requests: import('./types').DocumentRequest[] }>(`/api/projects/${projectId}/document-requests/order`, { orderedIds }),
  applyDocumentRequestTemplate: (projectId: string, templateId: string, appliesTo: 'all' | string[] = 'all') =>
    post<{ requests: import('./types').DocumentRequest[] }>(`/api/projects/${projectId}/document-requests/apply-template`, { templateId, appliesTo }),
  documentRequestTemplates: () =>
    get<{ templates: import('./types').DocumentRequestTemplate[] }>('/api/document-request-templates'),
  createDocumentRequestTemplate: (body: { name: string; body: import('./types').DocumentRequestTemplate['body'] }) =>
    post<import('./types').DocumentRequestTemplate>('/api/document-request-templates', body),
  // Consoles & mail wave (6 Oct 2026): renaming/editing a workspace's own
  // template — the route already existed (src/api/routes/document-requests.js
  // PATCH), this is its first client-side caller.
  updateDocumentRequestTemplate: (
    templateId: string,
    body: Partial<{ name: string; body: import('./types').DocumentRequestTemplate['body'] }>,
  ) => patch<import('./types').DocumentRequestTemplate>(`/api/document-request-templates/${encodeURIComponent(templateId)}`, body),
  removeDocumentRequestTemplate: (templateId: string) =>
    del<{ ok: true }>(`/api/document-request-templates/${encodeURIComponent(templateId)}`),

  matrix: (projectId: string, refresh = false) =>
    refresh
      ? post<Matrix>(`/api/projects/${projectId}/tender/matrix`, {})
      : get<Matrix>(`/api/projects/${projectId}/tender/matrix`),
  questions: (projectId: string) => post<{ vendors: QuestionBlock[] }>(`/api/projects/${projectId}/tender/questions`, {}),
  demoScript: (projectId: string) => post<import('./types').DemoScript>(`/api/projects/${projectId}/tender/demo-script`, {}),
  tco: (projectId: string) => post<TcoReport>(`/api/projects/${projectId}/tender/tco`, {}),
  demoTicks: (projectId: string) => get<{ ticks: Record<string, boolean> }>(`/api/projects/${projectId}/tender/demo-ticks`),
  saveDemoTicks: (projectId: string, ticks: Record<string, boolean>) =>
    put<{ ticks: Record<string, boolean> }>(`/api/projects/${projectId}/tender/demo-ticks`, { ticks }),

  // --- AI usage ---------------------------------------------------------------
  aiUsage: () => get<import('./types').AiUsageSummary>('/api/ai/usage'),
  /**
   * Only when `usage.purchasing.available` is true (see UsagePanel.tsx) —
   * gated server-side to the workspace's own instance admin and to a
   * hosted deployment with a live Stripe key regardless of what the client
   * believes (src/api/routes/billing.js). `url` is Stripe's own hosted
   * Checkout page; nothing here ever sees a card number.
   */
  buyCredits: (body: { kind: import('./types').AiUsageKind; packs: number }) =>
    post<{ ok: true; url: string; id: string }>('/api/billing/credits/checkout', body),
  /** The pack price and per-kind weights for the "Buy credits" dialog — see UsagePanel.tsx. */
  billingStatus: () => get<import('./types').BillingStatus>('/api/billing/status'),
  /**
   * On a deployment with no self-serve purchasing (private, dedicated,
   * on-premise, or an unset deployment kind), or a hosted one Stripe is not
   * yet configured on: this raises a request visible only to the
   * requester's own tenant's instance admin — never the product operator.
   * See src/api/routes/ai-usage.js and InstanceAdminScreen.tsx.
   */
  requestAiCredits: (body: { kind?: string; note?: string }) =>
    post<{ id: string }>('/api/ai/credit-request', body),

  // --- notifications ----------------------------------------------------------
  notifications: () => get<NotificationsPayload>('/api/notifications'),
  markNotificationsRead: (ids?: string[]) =>
    post<NotificationsPayload>('/api/notifications/read', ids ? { ids } : {}),

  // --- vendor portal --------------------------------------------------------
  portalPack: (projectId: string) => get<PortalPack>(`/api/projects/${projectId}/portal-invites`),
  createPortalInvite: (projectId: string, body: { vendorId: string; contactName?: string; contactEmail?: string; dueAt?: string | null }) =>
    post<import('./types').PortalInvite>(`/api/projects/${projectId}/portal-invites`, body),
  setPortalInviteDueDate: (projectId: string, inviteId: string, dueAt: string | null) =>
    patch<import('./types').PortalInvite>(`/api/projects/${projectId}/portal-invites/${encodeURIComponent(inviteId)}`, { dueAt }),
  revokePortalInvite: (projectId: string, inviteId: string) =>
    del<{ ok: true }>(`/api/projects/${projectId}/portal-invites/${inviteId}`),
  reopenPortalInvite: (projectId: string, inviteId: string) =>
    post<import('./types').PortalInvite>(`/api/projects/${projectId}/portal-invites/${encodeURIComponent(inviteId)}/reopen`),
  /** No token change — the vendor's existing link keeps working. */
  remindPortalInvite: (projectId: string, inviteId: string) =>
    post<import('./types').PortalInvite>(`/api/projects/${projectId}/portal-invites/${encodeURIComponent(inviteId)}/remind`),
  /** Destructive: rotates the token, so the old link stops working. */
  reissuePortalInvite: (projectId: string, inviteId: string) =>
    post<import('./types').PortalInvite & { link: string }>(`/api/projects/${projectId}/portal-invites/${encodeURIComponent(inviteId)}/reissue`),
  portalView: (token: string) => get<PortalView>(`/api/portal/${encodeURIComponent(token)}`, { anonymous: true }),
  portalSave: (token: string, responses: Array<Partial<VendorResponse>>) =>
    put<{ ok: true; saved: number; responses: VendorResponse[] }>(
      `/api/portal/${encodeURIComponent(token)}/responses`,
      { responses },
      { anonymous: true },
    ),
  portalUpload: (token: string, body: { kind: string; filename: string; text?: string; contentBase64?: string; requestId?: string | null }) =>
    post<{ id: string; clipped?: boolean; requestId?: string | null }>(`/api/portal/${encodeURIComponent(token)}/documents`, body, { anonymous: true }),
  /** One non-file checklist item's answer — autosaved on a debounce, same pattern as portalSave. */
  portalSaveAnswer: (token: string, requestId: string, value: string) =>
    put<{ ok: true }>(`/api/portal/${encodeURIComponent(token)}/answers/${encodeURIComponent(requestId)}`, { value }, { anonymous: true }),
  portalSubmit: (token: string) =>
    post<{ ok: true; submittedAt: string }>(`/api/portal/${encodeURIComponent(token)}/submit`, {}, { anonymous: true }),

  // --- report -----------------------------------------------------------------
  report: (projectId: string) => get<ReportModel>(`/api/projects/${projectId}/report`),

  // --- decision -------------------------------------------------------------
  signoffs: (projectId: string) => get<{ signoffs: Signoff[] }>(`/api/projects/${projectId}/signoffs`),
  addSignoff: (projectId: string, body: { meaning: string; reason: string; password?: string }) =>
    post<Signoff>(`/api/projects/${projectId}/signoffs`, body),
  expertReview: (projectId: string, body: { scope?: string; message: string; name?: string; email?: string }) =>
    post<{ id: string; status: string }>(`/api/projects/${projectId}/expert-review`, body),

  // --- drafting -------------------------------------------------------------
  draftProject: draftProjectStream,

  // --- URS step ---------------------------------------------------------------
  extractUrsRows: extractUrsRowsStream,
};

/**
 * The draft engine's own wording for a failure that carries no message from
 * the server — this module has no locale context (see `PopupBlockedError`
 * below), so it throws which *kind* of nothing happened and leaves the
 * sentence to the caller, the same division of labour `UnreadableFileError`
 * uses in pdf-text.ts.
 *
 * `kind` distinguishes three different shapes of "the draft did not work":
 * - `failed` — the request itself failed (bad response, or an SSE `error`
 *   frame) and the server gave no message to forward.
 * - `unreadable` — the response claimed to stream but no reader could be
 *   opened on it.
 * - `incomplete` — the stream ended cleanly with no `result` frame at all.
 */
export class DraftError extends Error {
  readonly kind: 'failed' | 'unreadable' | 'incomplete';

  constructor(kind: 'failed' | 'unreadable' | 'incomplete') {
    super(kind);
    this.name = 'DraftError';
    this.kind = kind;
  }
}

/**
 * The draft engine streams progress over SSE. We read the stream so the
 * timeline moves in step with the server instead of guessing.
 */
async function draftProjectStream(
  body: { documents: Array<{ name: string; kind: string; text: string }>; hints?: Record<string, string> },
  onProgress?: (event: { id: string; state: string; detail?: string }) => void,
): Promise<DraftResult> {
  const res = await fetch('/api/ai/draft-project/stream', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream, application/json',
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const data = await parse(res);
    const message = data && (data.error || data.message);
    if (message) throw new ApiError(message, res.status, data?.code, data);
    throw new DraftError('failed');
  }

  const contentType = res.headers.get('content-type') || '';
  if (!/text\/event-stream/i.test(contentType)) {
    return (await parse(res)) as DraftResult;
  }

  const reader = res.body?.getReader();
  if (!reader) throw new DraftError('unreadable');
  const decoder = new TextDecoder();
  let buffer = '';
  let result: DraftResult | null = null;
  let failure: { error?: string; code?: string; status?: number } | null = null;

  const handleFrame = (raw: string) => {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of raw.split(/\r?\n/)) {
      if (!line || line.startsWith(':')) continue;
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
    }
    if (!dataLines.length) return;
    const payload = JSON.parse(dataLines.join('\n'));
    if (event === 'progress') onProgress?.(payload);
    else if (event === 'result') result = payload;
    else if (event === 'error') failure = payload;
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    buffer = buffer.replace(/\r\n/g, '\n');
    let index = buffer.indexOf('\n\n');
    while (index !== -1) {
      handleFrame(buffer.slice(0, index));
      buffer = buffer.slice(index + 2);
      index = buffer.indexOf('\n\n');
    }
  }
  if (buffer.trim()) handleFrame(buffer);

  if (failure) {
    const reason = failure as { error?: string; code?: string; status?: number };
    if (reason.error) throw new ApiError(reason.error, reason.status || 500, reason.code);
    throw new DraftError('failed');
  }
  if (!result) throw new DraftError('incomplete');
  return result;
}

/**
 * The URS step's own extraction call: same SSE shape as `draftProjectStream`
 * above (progress frames, then one `result` or `error` frame), against the
 * project-scoped endpoint instead of the project-creation one, and with a
 * `signal` so the Extracting screen's Cancel button can actually stop
 * waiting on it. The server keeps running to completion either way — there
 * is no mid-step abort on that side — but the point of "cancellable" here is
 * that the person is never stuck watching a page that ignores them; aborting
 * the fetch does that without needing the server to cooperate.
 */
async function extractUrsRowsStream(
  projectId: string,
  documents: Array<{ name: string; kind: string; text: string }>,
  onProgress?: (event: { id: string; state: string; detail?: string }) => void,
  signal?: AbortSignal,
): Promise<UrsExtractResult> {
  const res = await fetch(`/api/projects/${projectId}/urs/extract/stream`, {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream, application/json',
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
    body: JSON.stringify({ documents }),
  });

  if (!res.ok) {
    const data = await parse(res);
    const message = data && (data.error || data.message);
    if (message) throw new ApiError(message, res.status, data?.code, data);
    throw new DraftError('failed');
  }

  const contentType = res.headers.get('content-type') || '';
  if (!/text\/event-stream/i.test(contentType)) {
    return (await parse(res)) as UrsExtractResult;
  }

  const reader = res.body?.getReader();
  if (!reader) throw new DraftError('unreadable');
  const decoder = new TextDecoder();
  let buffer = '';
  let result: UrsExtractResult | null = null;
  let failure: { error?: string; code?: string; status?: number } | null = null;

  const handleFrame = (raw: string) => {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of raw.split(/\r?\n/)) {
      if (!line || line.startsWith(':')) continue;
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
    }
    if (!dataLines.length) return;
    const payload = JSON.parse(dataLines.join('\n'));
    if (event === 'progress') onProgress?.(payload);
    else if (event === 'result') result = payload;
    else if (event === 'error') failure = payload;
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      buffer = buffer.replace(/\r\n/g, '\n');
      let index = buffer.indexOf('\n\n');
      while (index !== -1) {
        handleFrame(buffer.slice(0, index));
        buffer = buffer.slice(index + 2);
        index = buffer.indexOf('\n\n');
      }
    }
  } catch (err) {
    // An aborted fetch rejects the read with this name — the caller already
    // knows it cancelled, so there is nothing to throw here.
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { rows: [], warnings: [] };
    }
    throw err;
  }
  if (buffer.trim()) handleFrame(buffer);

  if (failure) {
    const reason = failure as { error?: string; code?: string; status?: number };
    if (reason.error) throw new ApiError(reason.error, reason.status || 500, reason.code);
    throw new DraftError('failed');
  }
  if (!result) throw new DraftError('incomplete');
  return result;
}

/**
 * A document the app builds or serves on demand failed to come back, and
 * nothing in the response said why — this module has no locale context (see
 * `PopupBlockedError` below), so it throws *which* resource failed and
 * leaves the sentence to the caller, the same division of labour
 * `UnreadableFileError` uses in pdf-text.ts. When the server does give a
 * reason (`data.error`), that is forwarded as an ordinary `ApiError`
 * instead — this type exists only for the case where api.ts would otherwise
 * have had to invent the English itself.
 */
export class DownloadError extends Error {
  readonly kind:
    | 'decisionPack'
    | 'reportDeck'
    | 'responseTemplate'
    | 'portalTemplate'
    | 'sourceDocument'
    | 'scorecardCsv'
    | 'rtmCsv';

  constructor(kind: DownloadError['kind']) {
    super(kind);
    this.name = 'DownloadError';
    this.kind = kind;
  }
}

/** Parse the body of a failed download response, if any, for a server-given reason. */
async function downloadFailure(res: Response, kind: DownloadError['kind']): Promise<never> {
  const data = await parse(res);
  if (data && typeof data === 'object' && 'error' in data && (data as { error?: string }).error) {
    throw new ApiError((data as { error: string }).error, res.status);
  }
  throw new DownloadError(kind);
}

/** The decision pack is HTML, opened in a new tab rather than parsed. */
export async function fetchDecisionPack(projectId: string): Promise<Blob> {
  const res = await fetch(`/api/projects/${projectId}/decision-pack`, {
    credentials: 'include',
    headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
  });
  if (!res.ok) return downloadFailure(res, 'decisionPack');
  return res.blob();
}

/** The overview deck is HTML too — same hand-off as the decision pack: fetch
 * as a blob, open in a new tab, and the browser's own Print dialog is how
 * either becomes a PDF. */
export async function fetchReportDeck(projectId: string): Promise<Blob> {
  const res = await fetch(`/api/projects/${projectId}/report/deck`, {
    credentials: 'include',
    headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
  });
  if (!res.ok) return downloadFailure(res, 'reportDeck');
  return res.blob();
}

/**
 * `openBlobInNewTab` failed because the browser would not open the blank tab
 * at all — a pop-up blocker, not the fetch. This module sits below the React
 * tree (no `useI18n()` here), so it cannot pick the localised copy itself;
 * it throws this sentinel and leaves the message to the caller, the same
 * division of labour `UnreadableFileError` uses in pdf-text.ts.
 */
export class PopupBlockedError extends Error {
  constructor() {
    super('popup-blocked');
    this.name = 'PopupBlockedError';
  }
}

/**
 * Fetch a blob and hand it to a new tab, without losing the click that
 * authorized opening one. `window.open()` only counts as user-initiated if
 * it runs inside the same synchronous tick as the gesture that triggered
 * it — calling it after an `await fetch(...)` resolves runs it a macrotask
 * or more later, by which point every major browser's popup blocker treats
 * it as an unrequested pop-up and silently drops it.
 *
 * The fix is to open the tab immediately (still inside the click handler,
 * blank), and only point it at the real URL once the fetch resolves. Used by
 * the report's and the decision pack's "open" actions.
 */
export async function openBlobInNewTab(fetcher: () => Promise<Blob>): Promise<void> {
  const tab = window.open('', '_blank');
  if (!tab) {
    throw new PopupBlockedError();
  }
  try {
    const blob = await fetcher();
    const url = URL.createObjectURL(blob);
    // The tab was opened without `noopener` so we could still navigate it
    // ourselves; sever the back-reference now that it has somewhere to go,
    // the same protection `noopener` would have given a same-tick open.
    tab.opener = null;
    tab.location.href = url;
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab.close();
    throw err;
  }
}

export async function fetchResponseTemplate(projectId: string): Promise<Blob> {
  const res = await fetch(`/api/projects/${projectId}/tender/template.csv`, {
    credentials: 'include',
    headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
  });
  if (!res.ok) return downloadFailure(res, 'responseTemplate');
  return res.blob();
}

/** The portal's own copy of the response template — no session, a token in the URL. */
export async function fetchPortalTemplate(token: string): Promise<Blob> {
  const res = await fetch(`/api/portal/${encodeURIComponent(token)}/template.csv`);
  if (!res.ok) return downloadFailure(res, 'portalTemplate');
  return res.blob();
}

/** One of the evaluation's own requirement documents, from the vendor's own link. */
export async function fetchPortalSourceDocument(token: string, documentId: string): Promise<Blob> {
  const res = await fetch(`/api/portal/${encodeURIComponent(token)}/source/${encodeURIComponent(documentId)}`);
  if (!res.ok) return downloadFailure(res, 'sourceDocument');
  return res.blob();
}

/** The full scorecard, one row per stakeholder/section/criterion/vendor score. */
export async function fetchScorecardCsv(projectId: string): Promise<Blob> {
  const res = await fetch(`/api/projects/${projectId}/export.csv`, {
    credentials: 'include',
    headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
  });
  if (!res.ok) return downloadFailure(res, 'scorecardCsv');
  return res.blob();
}

/** The requirement traceability matrix: one row per requirement, every vendor's compliance and quote. */
export async function fetchRtmCsv(projectId: string): Promise<Blob> {
  const res = await fetch(`/api/projects/${projectId}/rtm.csv`, {
    credentials: 'include',
    headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
  });
  if (!res.ok) return downloadFailure(res, 'rtmCsv');
  return res.blob();
}
